// Parseo de GPX en el cliente para el visor de recorridos (ServiceRouteMap).
//
// El GPX de un servicio (generado por la lambda "GPX Rombo") trae, por cada
// <trkpt>: lat/lon, <ele> (elevación en m), <time> (ISO) y <cmt>Event: X</cmt>.
// No incluye velocidad explícita: se DERIVA de la distancia (haversine) entre
// puntos consecutivos dividida por el Δtiempo. También expone los <wpt>
// (START/ESCALA/END) con nombre/descripción.
//
// Se usa DOMParser (nativo) en vez de @mapbox/togeojson porque necesitamos
// ele+time por punto para velocidad/distancia, que togeojson no preserva de
// forma conveniente.

const R = 6371000 // radio terrestre (m)

const toRad = (d) => (d * Math.PI) / 180

// Distancia haversine en metros entre dos {lat,lng}.
export const haversine = (a, b) => {
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const textOf = (parent, tag) => {
  const el = parent.getElementsByTagName(tag)[0]
  return el ? el.textContent : null
}

// Parsea una cadena GPX y devuelve la estructura enriquecida:
// {
//   points: [{ lat, lng, ele, time(ms|null), event, dist(acum m), speed(km/h|null) }],
//   waypoints: [{ lat, lng, name, desc, kind: 'start'|'end'|'scale'|'waypoint' }],
//   bounds: [[minLng,minLat],[maxLng,maxLat]] | null,
//   totalDistance(m), totalDuration(s|null), maxSpeed, avgSpeed,
//   minEle, maxEle, name
// }
export const parseGpx = (gpxString) => {
  const empty = {
    points: [], waypoints: [], bounds: null,
    totalDistance: 0, totalDuration: null,
    maxSpeed: null, avgSpeed: null, minEle: null, maxEle: null, name: null,
  }
  if (!gpxString || typeof gpxString !== "string") return empty

  let doc
  try {
    doc = new DOMParser().parseFromString(gpxString, "application/xml")
  } catch (_e) {
    return empty
  }
  if (!doc || doc.getElementsByTagName("parsererror").length > 0) return empty

  const name = textOf(doc, "name")

  // --- Trackpoints -----------------------------------------------------------
  const trkptEls = Array.from(doc.getElementsByTagName("trkpt"))
  const points = []
  let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity
  let minEle = Infinity, maxEle = -Infinity

  for (const el of trkptEls) {
    const lat = num(el.getAttribute("lat"))
    const lng = num(el.getAttribute("lon"))
    if (lat === null || lng === null) continue
    const ele = num(textOf(el, "ele"))
    const timeStr = textOf(el, "time")
    const time = timeStr ? Date.parse(timeStr) : null
    const cmt = textOf(el, "cmt") || ""
    const event = cmt.replace(/^Event:\s*/i, "").trim() || null
    points.push({
      lat, lng,
      ele: ele,
      time: Number.isFinite(time) ? time : null,
      event,
      dist: 0,
      speed: null,
    })
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
    if (ele !== null) {
      if (ele < minEle) minEle = ele
      if (ele > maxEle) maxEle = ele
    }
  }

  // Distancia acumulada + velocidad derivada (km/h) por punto.
  let totalDistance = 0
  let maxSpeed = 0
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const cur = points[i]
    const d = haversine(prev, cur) // m
    totalDistance += d
    cur.dist = totalDistance
    if (prev.time != null && cur.time != null) {
      const dtSec = (cur.time - prev.time) / 1000
      if (dtSec > 0) {
        const kmh = (d / dtSec) * 3.6
        cur.speed = kmh
        if (kmh > maxSpeed) maxSpeed = kmh
      }
    }
  }

  const first = points[0]
  const last = points[points.length - 1]
  const totalDuration =
    first?.time != null && last?.time != null
      ? (last.time - first.time) / 1000
      : null
  const avgSpeed =
    totalDuration && totalDuration > 0
      ? (totalDistance / totalDuration) * 3.6
      : null

  // --- Waypoints -------------------------------------------------------------
  const wptEls = Array.from(doc.getElementsByTagName("wpt"))
  const waypoints = wptEls.map((el) => {
    const lat = num(el.getAttribute("lat"))
    const lng = num(el.getAttribute("lon"))
    const wname = textOf(el, "name") || ""
    const desc = textOf(el, "desc") || ""
    const timeStr = textOf(el, "time")
    const time = timeStr ? Date.parse(timeStr) : null
    const d = `${desc} ${wname}`.toUpperCase()
    let kind = "waypoint"
    if (d.includes("START") || d.includes("ORIGEN")) kind = "start"
    else if (d.includes("END") || d.includes("DESTINO")) kind = "end"
    else if (d.includes("ESCALA") || d.includes("SCALE")) kind = "scale"
    return { lat, lng, name: wname, desc, kind, time: Number.isFinite(time) ? time : null }
  }).filter((w) => w.lat !== null && w.lng !== null)

  const bounds =
    points.length > 0
      ? [[minLng, minLat], [maxLng, maxLat]]
      : null

  return {
    points,
    waypoints,
    bounds,
    totalDistance,
    totalDuration,
    maxSpeed: maxSpeed || null,
    avgSpeed,
    minEle: Number.isFinite(minEle) ? minEle : null,
    maxEle: Number.isFinite(maxEle) ? maxEle : null,
    name,
  }
}

// Construye la estructura "parsed" (misma forma que parseGpx) a partir de una
// lista simple de puntos [{lat,lng}], p. ej. un tracking polyline5 ya decodificado
// (contrato GRS). No hay tiempo/elevación por punto, así que la velocidad queda
// null; sí se calcula distancia acumulada y bounds para pintar y encuadrar.
export const pointsToParsed = (rawPoints) => {
  const empty = {
    points: [], waypoints: [], bounds: null,
    totalDistance: 0, totalDuration: null,
    maxSpeed: null, avgSpeed: null, minEle: null, maxEle: null, name: null,
  }
  const src = (rawPoints || []).filter(
    (p) => p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)),
  )
  if (!src.length) return empty

  const points = src.map((p) => ({
    lat: Number(p.lat), lng: Number(p.lng),
    ele: null, time: null, event: null, dist: 0, speed: null,
  }))

  let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity
  let totalDistance = 0
  for (let i = 0; i < points.length; i++) {
    const cur = points[i]
    if (i > 0) {
      totalDistance += haversine(points[i - 1], cur)
      cur.dist = totalDistance
    }
    if (cur.lat < minLat) minLat = cur.lat
    if (cur.lat > maxLat) maxLat = cur.lat
    if (cur.lng < minLng) minLng = cur.lng
    if (cur.lng > maxLng) maxLng = cur.lng
  }

  return {
    ...empty,
    points,
    bounds: [[minLng, minLat], [maxLng, maxLat]],
    totalDistance,
  }
}

// Construye la estructura "parsed" a partir del `path[]` que devuelve GRS en el
// contrato actualizado (/grs/audit y /grs/reconstruction). Cada punto trae
// { seq, idx, lat, lon, t(ISO|epoch), speed(m/s), heading, acc, action }.
// A diferencia de pointsToParsed, aquí SÍ hay tiempo y velocidad por punto, lo
// que habilita el replay con marca temporal real. speed se asume en m/s y se
// convierte a km/h; si viniera ya en km/h el valor seguiría siendo coherente al
// mostrarse (se documenta el supuesto). acc:null marca punto reconstruido.
export const pathToParsed = (rawPath) => {
  const empty = {
    points: [], waypoints: [], bounds: null,
    totalDistance: 0, totalDuration: null,
    maxSpeed: null, avgSpeed: null, minEle: null, maxEle: null, name: null,
  }
  const src = (rawPath || []).filter(
    (p) => p && Number.isFinite(Number(p.lat)) &&
      Number.isFinite(Number(p.lon ?? p.lng)),
  )
  if (!src.length) return empty

  const toMs = (t) => {
    if (t == null) return null
    if (typeof t === "number") return t > 1e12 ? t : t * 1000 // epoch s|ms
    const parsed = Date.parse(t)
    return Number.isFinite(parsed) ? parsed : null
  }

  const points = src.map((p) => {
    const spd = Number(p.speed)
    return {
      lat: Number(p.lat), lng: Number(p.lon ?? p.lng),
      ele: null,
      time: toMs(p.t ?? p.time),
      event: p.action || null,
      // acc === null (explícito) => punto reconstruido por GRS.
      reconstructed: p.acc === null,
      heading: Number.isFinite(Number(p.heading)) ? Number(p.heading) : null,
      dist: 0,
      // speed de GRS está en m/s -> km/h.
      speed: Number.isFinite(spd) ? spd * 3.6 : null,
    }
  })

  let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity
  let totalDistance = 0
  let maxSpeed = 0
  for (let i = 0; i < points.length; i++) {
    const cur = points[i]
    if (i > 0) {
      totalDistance += haversine(points[i - 1], cur)
      cur.dist = totalDistance
    }
    if (cur.speed != null && cur.speed > maxSpeed) maxSpeed = cur.speed
    if (cur.lat < minLat) minLat = cur.lat
    if (cur.lat > maxLat) maxLat = cur.lat
    if (cur.lng < minLng) minLng = cur.lng
    if (cur.lng > maxLng) maxLng = cur.lng
  }

  const first = points[0]
  const last = points[points.length - 1]
  const totalDuration =
    first?.time != null && last?.time != null
      ? (last.time - first.time) / 1000
      : null
  const avgSpeed =
    totalDuration && totalDuration > 0
      ? (totalDistance / totalDuration) * 3.6
      : null

  return {
    ...empty,
    points,
    bounds: [[minLng, minLat], [maxLng, maxLat]],
    totalDistance,
    totalDuration,
    maxSpeed: maxSpeed || null,
    avgSpeed,
  }
}

// Convierte los puntos a un LineString GeoJSON (para la capa de la ruta).
export const pointsToLineString = (points) => ({
  type: "Feature",
  properties: {},
  geometry: {
    type: "LineString",
    coordinates: points.map((p) => [p.lng, p.lat]),
  },
})

// Segmenta la ruta en tramos coloreados por velocidad (para pintar la línea con
// gradiente por velocidad). Devuelve un FeatureCollection de LineStrings de 2
// puntos, cada uno con properties.speed (km/h).
export const pointsToSpeedSegments = (points) => ({
  type: "FeatureCollection",
  features: points.slice(1).map((p, i) => ({
    type: "Feature",
    properties: { speed: p.speed ?? 0 },
    geometry: {
      type: "LineString",
      coordinates: [
        [points[i].lng, points[i].lat],
        [p.lng, p.lat],
      ],
    },
  })),
})

// Genera marcadores de distancia cada `stepKm` km a lo largo de la ruta.
// Devuelve [{ lat, lng, km }] interpolando entre los puntos.
export const distanceMarkers = (points, stepKm = 1) => {
  if (!points || points.length < 2 || stepKm <= 0) return []
  const stepM = stepKm * 1000
  const markers = []
  let nextThreshold = stepM
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const cur = points[i]
    while (cur.dist >= nextThreshold) {
      // Interpola linealmente entre prev y cur según el umbral.
      const span = cur.dist - prev.dist
      const t = span > 0 ? (nextThreshold - prev.dist) / span : 0
      markers.push({
        lat: prev.lat + (cur.lat - prev.lat) * t,
        lng: prev.lng + (cur.lng - prev.lng) * t,
        km: Math.round(nextThreshold / 1000),
      })
      nextThreshold += stepM
    }
  }
  return markers
}
